import { useMemo, useState } from "react";

import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import DeleteIcon from "@mui/icons-material/Delete";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { Typography } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

import PropTypes from "prop-types";

import { DiscountAccountsDialog } from "@/features/outlet-chart-of-accounts";
import { formatDate, formatNumber } from "@/features/shared";
import { TwoLineCell, useSnackbar } from "@/features/ui";

const sumDiscountAccounts = (discountAccounts) =>
  (discountAccounts ?? []).reduce(
    (acc, account) => acc + (account.amount || 0),
    0
  );

export const PurchaseInvoicePaymentsSection = ({ readOnly = false }) => {
  const { control, setValue } = useFormContext();
  const { showSnackbar } = useSnackbar();

  const { remove } = useFieldArray({
    control,
    name: "purchaseInvoicePayments",
  });

  const [purchaseInvoicePayments, buyer] = useWatch({
    control,
    name: ["purchaseInvoicePayments", "buyer"],
  });

  const [discountRowId, setDiscountRowId] = useState(null);
  const discountRow = purchaseInvoicePayments?.find(
    (payment) => payment.id === discountRowId
  );

  const handleDeleteClick = (id) => () => {
    // 🔍 Find index of the row with the matching id
    const index = purchaseInvoicePayments.findIndex(
      (variant) => variant.id === id
    );

    if (index !== -1) {
      remove(index);
      showSnackbar("Baris faktur berhasil dihapus dari daftar", "success");
    }
  };

  const handleSaveDiscountAccounts = (discountAccounts) => {
    const index = purchaseInvoicePayments.findIndex(
      (payment) => payment.id === discountRowId
    );

    if (index !== -1) {
      // Update only the specific sub-paths instead of rewriting the whole
      // field-array item — rewriting the item (with its nested discountAccounts)
      // via useFieldArray.update corrupts the row on repeated edits.
      setValue(
        `purchaseInvoicePayments.${index}.discountAccounts`,
        discountAccounts,
        {
          shouldDirty: true,
        }
      );
      setValue(
        `purchaseInvoicePayments.${index}.discount`,
        sumDiscountAccounts(discountAccounts),
        {
          shouldDirty: true,
        }
      );
    }
  };

  // DataGrid freezes whatever array it gets as `rows` (a dev-only guard against
  // rows.push()). useWatch hands back react-hook-form's own array, and freezing
  // that breaks every later write, so give the grid a copy.
  const rows = useMemo(() => [...purchaseInvoicePayments], [purchaseInvoicePayments]);

  const columns = [
    {
      field: "referenceNumber",
      headerName: "No. Faktur #",
      flex: 2,
      renderCell: ({ row }) => (
        <TwoLineCell
          primary={row.referenceNumber}
          secondary={row.sellerReferenceNumber}
        />
      ),
    },
    {
      field: "transactionDate",
      headerName: "Tanggal",
      flex: 1,
      valueFormatter: (value) => formatDate(value),
    },
    {
      field: "grandTotal",
      headerName: "Total Faktur",
      type: "number",
      flex: 1,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "outstandingAmount",
      headerName: "Terutang",
      type: "number",
      flex: 1,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "allocatedAmount",
      headerName: "Bayar",
      type: "number",
      flex: 1,
      editable: !readOnly,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "discount",
      headerName: "Diskon",
      type: "number",
      flex: 1,
      // Discount is derived from the discount accounts attached to the row.
      valueGetter: (_, row) => sumDiscountAccounts(row.discountAccounts),
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "paidAmount",
      headerName: "Total Bayar",
      type: "number",
      flex: 1,
      // Paid amount is derived from the allocated amount and discount.
      valueGetter: (_, row) =>
        (row.allocatedAmount || 0) - sumDiscountAccounts(row.discountAccounts),
      valueFormatter: (value) => formatNumber(value),
    },
    // Dropped entirely when frozen rather than rendered empty: the amount is not
    // editable, the row cannot be removed, and its discount accounts cannot be
    // re-mapped, so the column would only take up width.
    ...(readOnly
      ? []
      : [
          {
            field: "actions",
            type: "actions",
            headerName: "Aksi",
            getActions: ({ id }) => [
              <GridActionsCellItem
                key={`discount-${id}`}
                icon={<LocalOfferIcon />}
                label="Informasi Diskon"
                className="textPrimary"
                onClick={() => setDiscountRowId(id)}
                color="primary"
              />,
              <GridActionsCellItem
                key={`delete-${id}`}
                icon={<DeleteIcon />}
                label="Hapus"
                className="textPrimary"
                onClick={handleDeleteClick(id)}
                color="error"
              />,
            ],
          },
        ]),
  ];

  const processRowUpdate = async (newRow) => {
    return new Promise((resolve, reject) => {
      const { allocatedAmount, maxAmount } = newRow;

      // Validation logic
      if (allocatedAmount > maxAmount) {
        reject(
          new Error(
            `Jumlah alokasi tidak boleh lebih besar dari maksimum (max: ${maxAmount})`
          )
        );
        return;
      }

      if (allocatedAmount < 0) {
        reject(new Error("Jumlah alokasi tidak boleh kurang dari 0"));
        return;
      }

      // Find the index and update the form state. Only write the edited
      // field so the row's other data (and nested discountAccounts) stay intact.
      const index = purchaseInvoicePayments.findIndex(
        (payment) => payment.id === newRow.id
      );

      if (index !== -1) {
        setValue(
          `purchaseInvoicePayments.${index}.allocatedAmount`,
          allocatedAmount,
          {
            shouldDirty: true,
          }
        );
        resolve(newRow);
      } else {
        reject(new Error("Baris pembayaran tidak ditemukan. Muat ulang halaman lalu coba lagi."));
      }
    });
  };

  const handleProcessRowUpdateError = (error) => {
    showSnackbar(error.message, "error");
  };

  return (
    <>
      <Typography variant="h6">Pembayaran Faktur</Typography>
      <DataGrid
        getRowHeight={() => "auto"}
        editMode="row"
        columns={columns}
        rows={rows}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        initialState={{
          sorting: {
            sortModel: [{ field: "referenceNumber", sort: "asc" }],
          },
        }}
      />
      {discountRow && (
        <DiscountAccountsDialog
          key={discountRowId}
          open
          onClose={() => setDiscountRowId(null)}
          onSave={handleSaveDiscountAccounts}
          outletId={buyer?.id}
          referenceNumber={discountRow.referenceNumber}
          maxAmount={discountRow.allocatedAmount}
          value={discountRow.discountAccounts}
        />
      )}
    </>
  );
};

PurchaseInvoicePaymentsSection.propTypes = {
  readOnly: PropTypes.bool,
};
